import { createModalFocusScope } from '../../components/modal-focus-scope.js';
import { canAnimateElement, readCssTimeMs } from '../../components/motion-policy.js';
import { cancelAnimations, playAnimation, settleAnimations } from '../../components/motion-runner.js';

export function initGameDetailsNoteAttachmentPreview(
    documentRef = globalThis.document,
    windowRef = globalThis.window,
) {
    const modal = documentRef?.querySelector?.('[data-game-note-attachment-preview]');
    if (!(modal instanceof HTMLElement)) {
        return null;
    }

    const dialog = modal.querySelector('[data-game-note-attachment-preview-dialog]');
    const stage = modal.querySelector('[data-game-note-attachment-preview-stage]');
    const error = modal.querySelector('[data-game-note-attachment-preview-error]');
    const filename = modal.querySelector('[data-game-note-attachment-preview-filename]');
    const previousButton = modal.querySelector('[data-game-note-attachment-preview-previous]');
    const nextButton = modal.querySelector('[data-game-note-attachment-preview-next]');
    const position = modal.querySelector('[data-game-note-attachment-preview-position]');
    const backdrop = modal.querySelector('[data-game-note-attachment-preview-backdrop]');
    if (
        !(dialog instanceof HTMLElement)
        || !(stage instanceof HTMLElement)
        || !(error instanceof HTMLElement)
        || !(filename instanceof HTMLElement)
        || !(previousButton instanceof HTMLButtonElement)
        || !(nextButton instanceof HTMLButtonElement)
        || !(position instanceof HTMLElement)
        || !(backdrop instanceof HTMLButtonElement)
    ) {
        return null;
    }

    let activeLink = null;
    let activateLink = null;
    let renderedImage = null;
    let renderedOrigin = null;
    let imageTransitionId = 0;
    let imageAnimations = [];
    let isClosing = false;

    const restoreImage = (image) => {
        if (!(image instanceof HTMLImageElement)) {
            return;
        }

        image.classList.remove('game-note-attachment-preview-image');
        if (
            renderedOrigin instanceof HTMLAnchorElement
            && renderedOrigin.isConnected
            && renderedOrigin.getAttribute('aria-disabled') !== 'true'
        ) {
            renderedOrigin.append(image);
        } else {
            image.remove();
        }
        renderedOrigin = null;
    };

    const finishClose = () => {
        imageTransitionId += 1;
        cancelAnimations(imageAnimations);
        imageAnimations = [];
        focusScope.deactivate();
        restoreImage(renderedImage);
        renderedImage = null;
        activeLink = null;
        activateLink = null;
        error.hidden = true;
        modal.hidden = true;
        isClosing = false;
    };

    const close = async () => {
        if (modal.hidden || isClosing) {
            return;
        }

        isClosing = true;
        imageTransitionId += 1;
        cancelAnimations(imageAnimations);
        imageAnimations = [];
        if (!canAnimateElement(dialog, windowRef)) {
            finishClose();
            return;
        }

        const duration = readCssTimeMs(dialog, '--game-note-preview-close-duration', 220);
        const animations = [
            playAnimation(backdrop, { opacity: [1, 0] }, {
                duration,
                easing: 'linear',
                fill: 'forwards',
            }),
            playAnimation(dialog, {
                opacity: [1, 0],
                transform: ['translateY(0) scale(1)', 'translateY(8px) scale(0.96)'],
            }, {
                duration,
                easing: 'cubic-bezier(0.4, 0, 1, 1)',
                fill: 'forwards',
            }),
        ].filter(Boolean);

        await settleAnimations(animations);
        cancelAnimations(animations);
        finishClose();
    };

    const focusScope = createModalFocusScope({ dialog, requestClose: close, documentRef });
    if (focusScope === null) {
        return null;
    }

    const readGalleryLinks = () => {
        if (!(activeLink instanceof HTMLAnchorElement)) {
            return [];
        }

        const stream = activeLink.closest('[data-game-notes-stream]');
        const root = stream instanceof HTMLElement ? stream : documentRef;
        const links = Array.from(root.querySelectorAll('[data-game-note-attachment-link]'))
            .filter((link) => link instanceof HTMLAnchorElement)
            .filter((link) => (
                link === activeLink
                || (
                    String(link.href ?? '').trim() !== ''
                    && link.getAttribute('aria-disabled') !== 'true'
                )
            ));

        return links.includes(activeLink) ? links : [activeLink];
    };

    const updateNavigation = () => {
        const links = readGalleryLinks();
        const index = links.indexOf(activeLink);
        const hasMultiple = links.length > 1 && index >= 0;
        previousButton.disabled = !hasMultiple;
        nextButton.disabled = !hasMultiple;
        position.hidden = !hasMultiple;
        position.textContent = hasMultiple ? `${index + 1} / ${links.length}` : '';
    };

    const commitImage = (link, image) => {
        restoreImage(renderedImage);
        renderedImage = null;
        error.hidden = true;

        if (!image.complete || image.naturalWidth === 0) {
            error.hidden = false;
            return;
        }

        renderedOrigin = link;
        image.classList.add('game-note-attachment-preview-image');
        stage.append(image);
        renderedImage = image;
    };

    const showImage = (link, image) => {
        const outgoingImage = renderedImage;
        const canTransition = modal.hidden === false
            && outgoingImage instanceof HTMLImageElement
            && outgoingImage !== image
            && canAnimateElement(outgoingImage, windowRef)
            && image.complete
            && image.naturalWidth > 0;
        const transitionId = ++imageTransitionId;
        cancelAnimations(imageAnimations);
        imageAnimations = [];

        if (!canTransition) {
            commitImage(link, image);
            return;
        }

        const duration = Math.round(readCssTimeMs(
            stage,
            '--game-note-preview-image-swap-duration',
            220,
        ) / 2);
        const easing = 'cubic-bezier(0.4, 0, 0.2, 1)';
        imageAnimations = [playAnimation(outgoingImage, [
            { opacity: 1, transform: 'scale(1)' },
            { opacity: 0, transform: 'scale(0.985)' },
        ], { duration, easing, fill: 'forwards' })].filter(Boolean);

        void settleAnimations(imageAnimations).then(async () => {
            if (transitionId !== imageTransitionId || modal.hidden || isClosing) {
                return;
            }

            cancelAnimations(imageAnimations);
            commitImage(link, image);
            imageAnimations = [playAnimation(image, [
                { opacity: 0, transform: 'scale(1.015)' },
                { opacity: 1, transform: 'scale(1)' },
            ], { duration, easing, fill: 'both' })].filter(Boolean);
            await settleAnimations(imageAnimations);
            if (transitionId === imageTransitionId) {
                cancelAnimations(imageAnimations);
                imageAnimations = [];
            }
        });
    };

    const open = ({ link, image, activateLink: activate }) => {
        if (
            isClosing
            || !(link instanceof HTMLAnchorElement)
            || !(image instanceof HTMLImageElement)
            || typeof activate !== 'function'
        ) {
            return false;
        }

        activeLink = link;
        activateLink = activate;
        const originalFilename = String(link.dataset.originalFilename ?? '').trim();
        filename.textContent = originalFilename;
        filename.hidden = originalFilename === '';
        if (originalFilename === '') {
            filename.removeAttribute('title');
        } else {
            filename.setAttribute('title', originalFilename);
        }

        updateNavigation();
        showImage(link, image);
        modal.hidden = false;
        focusScope.activate(link);
        windowRef.requestAnimationFrame(() => focusScope.focus());

        return true;
    };

    const navigate = (offset) => {
        const links = readGalleryLinks();
        const index = links.indexOf(activeLink);
        if (links.length < 2 || index < 0 || typeof activateLink !== 'function') {
            return;
        }

        activateLink(links[(index + offset + links.length) % links.length]);
    };

    previousButton.addEventListener('click', () => navigate(-1));
    nextButton.addEventListener('click', () => navigate(1));
    modal.querySelectorAll('[data-game-note-attachment-preview-close]').forEach((button) => {
        button.addEventListener('click', close);
    });
    backdrop.addEventListener('click', close);
    documentRef.addEventListener('keydown', (event) => {
        if (modal.hidden) {
            return;
        }
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            navigate(-1);
        } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            navigate(1);
        }
    });

    error.hidden = true;

    return { close, open };
}
